/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import './Layout.css';
import {
    AimOutlined,
    AlertOutlined,
    CarOutlined,
    DollarCircleOutlined,
    GiftOutlined,
    LoginOutlined,
    NodeIndexOutlined,
    PieChartOutlined,
    QuestionCircleOutlined,
    SettingOutlined,
    ShopOutlined,
    SoundOutlined,
    TeamOutlined,
    UserOutlined,
    BookOutlined,
    BarChartOutlined,
} from '@ant-design/icons';
import { MenuProps, Row, Col, Dropdown, Avatar, Badge, Image } from 'antd';
import { Breadcrumb, Layout, Menu } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useUserState } from '../Hooks/useUserState';
import { cleanAllCookies } from '../shared/Utils';
import useLogout from '../Hooks/useLogout';
import jwt_decode from 'jwt-decode';
import { useGetSettingQuery } from '../GraphQL/hooks/useGetSettingQuery';
import { useGetRegisterTaxiDriverQuery } from '../GraphQL/hooks/useGetRegisterTaxiDriverQuery';

const { Header, Content, Footer, Sider } = Layout;

const layout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [initRoot, setDefaultRoot] = useState<string>('/');
    const { ...state } = useUserState();
    const location = useLocation();

    const decoded: { _id: string; sectionRoles: [string] } = state?.accessToken
        ? jwt_decode(state?.accessToken)
        : undefined;

    const [pendingDrivers, setPendingDrivers] = useState(0);
    const { data: driversData } = useGetRegisterTaxiDriverQuery({ types: [], page: 1, limit: 100 });

    const { data, refetch, loading } = useGetSettingQuery(decoded?._id);
    const navigate = useNavigate();

    const logout = useLogout();

    const handelSignOut = async () => {
        await logout();
        localStorage.clear();
        cleanAllCookies();
        navigate('/login');
    };

    useEffect(() => {
        if (driversData) {
            const count = driversData.data.filter((d) => d.registerStatus === 'Pending').length;
            setPendingDrivers(count);
        }
    }, [driversData]);

    const items: MenuProps['items'] = [
        {
            key: 'welcome',
            label: (
                <a onClick={() => handleMenuItemClick('settings')}>
                    Welcome <strong>{data?.fullname}</strong>
                </a>
            ),
            icon: <UserOutlined />,
        },
        // notification ONLY if operator is STASIA Mobility
        data?.operatorId?.type === 'STASIA Mobility' &&
            pendingDrivers > 0 && {
                key: 'pendingDrivers',
                label: (
                    <a onClick={() => handleMenuItemClick('taxiDrivers')} style={{ color: 'red' }}>
                        🔔 {pendingDrivers > 100 ? `${pendingDrivers}+` : pendingDrivers} new taxi drivers pending
                        approval
                    </a>
                ),
            },
        {
            key: 'logout',
            label: <a onClick={() => handelSignOut()}>Log out</a>,
            icon: <LoginOutlined />,
        },
    ];

    const menuItems = [];

    if (decoded?.sectionRoles?.indexOf('Reports') > -1)
        menuItems.push({
            key: 'reports',
            icon: <PieChartOutlined />,
            label: <a onClick={() => handleMenuItemClick('reports')}>Reports</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Map') > -1)
        menuItems.push({
            key: 'map',
            icon: <AimOutlined />,
            label: <a onClick={() => handleMenuItemClick('map')}>Map Micro-Mobility</a>,
        });
    if (decoded?.sectionRoles?.indexOf('MapTaxi') > -1)
        menuItems.push({
            key: 'mapTaxi',
            icon: <AimOutlined />,
            label: <a onClick={() => handleMenuItemClick('mapTaxi')}>Map Taxi-Business</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Fleet') > -1)
        menuItems.push({
            key: 'fleet',
            icon: <CarOutlined />,
            label: <a onClick={() => handleMenuItemClick('fleet')}>Fleet</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Riders') > -1)
        menuItems.push({
            key: 'riders',
            icon: <TeamOutlined />,
            label: <a onClick={() => handleMenuItemClick('riders')}>Riders</a>,
        });
    if (decoded?.sectionRoles?.indexOf('TripsMicro') > -1)
        menuItems.push({
            key: 'tripsMicro',
            icon: <NodeIndexOutlined />,
            label: <a onClick={() => handleMenuItemClick('tripsMicro')}>Trips Micro</a>,
        });
    if (decoded?.sectionRoles?.indexOf('TripsTaxi') > -1)
        menuItems.push({
            key: 'tripsTaxi',
            icon: <NodeIndexOutlined />,
            label: <a onClick={() => handleMenuItemClick('tripsTaxi')}>Trips Taxi</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Promos') > -1)
        menuItems.push({
            key: 'promos',
            icon: <GiftOutlined />,
            label: <a onClick={() => handleMenuItemClick('promos')}>Promo/Coupon</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Campaign') > -1)
        menuItems.push({
            key: 'campaign',
            icon: <SoundOutlined />,
            label: <a onClick={() => handleMenuItemClick('campaign')}>Advt. Campaign</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Pricing') > -1)
        menuItems.push({
            key: 'pricing',
            icon: <DollarCircleOutlined />,
            label: <a onClick={() => handleMenuItemClick('pricing')}>Ride Pricing</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Passes') > -1)
        menuItems.push({
            key: 'passes',
            icon: <LoginOutlined />,
            label: <a onClick={() => handleMenuItemClick('passes')}>Passes Micro</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Operators') > -1)
        menuItems.push({
            key: 'operators',
            icon: <ShopOutlined />,
            label: <a onClick={() => handleMenuItemClick('operators')}>Operators</a>,
        });
    if (decoded?.sectionRoles?.indexOf('Bookings') > -1)
        menuItems.push({
            key: 'bookings',
            icon: <BookOutlined />,
            label: <a onClick={() => handleMenuItemClick('bookings')}>Bookings</a>,
        });
    if (decoded?.sectionRoles?.indexOf('TaxiDrivers') > -1)
        menuItems.push({
            key: 'taxiDrivers',
            icon: <CarOutlined />,
            label: <a onClick={() => handleMenuItemClick('taxiDrivers')}>Taxi Drivers</a>,
        });
    if (decoded?.sectionRoles?.indexOf('FinancialOverview') > -1)
        menuItems.push({
            key: 'financialOverview',
            icon: <BarChartOutlined />,
            label: <a onClick={() => handleMenuItemClick('financialOverview')}>Financial Overview</a>,
        });
    if (decoded?.sectionRoles?.indexOf('FleetProblem') > -1)
        menuItems.push({
            key: 'vehiculeProblem',
            icon: <AlertOutlined />,
            label: <a onClick={() => handleMenuItemClick('vehiculeProblem')}>Fleet Problems</a>,
        });
    menuItems.push({
        key: 'settings',
        icon: <SettingOutlined />,
        label: <a onClick={() => handleMenuItemClick('settings')}>Settings</a>,
    });
    menuItems.push({
        key: 'support',
        icon: <QuestionCircleOutlined />,
        label: <a onClick={() => handleMenuItemClick('support')}>Support</a>,
    });

    const handleMenuItemClick = (keyRoot: string) => {
        setDefaultRoot(keyRoot);
        navigate(`/${keyRoot}`);
    };

    const handelDefaultRoot = () => {
        let initRoot;
        if (location?.pathname == '/') {
            initRoot = menuItems[0].key;
        } else {
            initRoot = location.pathname?.slice(1);
        }
        setDefaultRoot(initRoot);
        navigate(`/${initRoot}`);
    };

    useEffect(() => {
        handelDefaultRoot();
    }, []);

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)} className="divToHide">
                <div className="logo">
                    <Avatar src={'./assets/mgt_logo.png'} />
                    <span className="citrin-text">CITRIN</span>
                    <span className="insights-text">Insights</span>
                </div>
                <Menu theme="dark" mode="inline" selectedKeys={[initRoot]} items={menuItems}></Menu>
            </Sider>
            <Layout className="site-layout">
                <Header className="site-layout-background divToHide" style={{ padding: 0 }}>
                    <Row>
                        <Col span={12}></Col>
                        <Col span={1} offset={11}>
                            <Dropdown menu={{ items }} trigger={['click']}>
                                <a onClick={(e) => e.preventDefault()}>
                                    <Badge count={pendingDrivers} offset={[0, 5]}>
                                        {data?.operatorId?.logo?.url ? (
                                            <Avatar size={32} src={<img src={data?.operatorId?.logo?.url} />} />
                                        ) : (
                                            <Avatar size={32} icon={<UserOutlined />} />
                                        )}
                                    </Badge>
                                </a>
                            </Dropdown>
                        </Col>
                    </Row>
                </Header>
                <Content style={{ margin: '0 16px' }}>
                    <Breadcrumb style={{ margin: '16px 0' }} className="divToHide"></Breadcrumb>
                    <div className="site-layout-background" style={{ padding: 24, minHeight: 360 }}>
                        <Outlet />
                    </div>
                </Content>
                <Footer className="divToHide" style={{ textAlign: 'center' }}>
                    CITRIN Dashboard ©{new Date().getFullYear()} Created by{' '}
                    <a href="https://www.stasia-digital.com/" target="_new">
                        STASIA Digital
                    </a>
                </Footer>
            </Layout>
        </Layout>
    );
};

export default layout;
